// The worker is imported as a URL only (a string, no code): pdf.js itself is
// loaded lazily below, the first time an owner picks a PDF (plan D12).
import pdfWorkerUrl from 'pdfjs-dist/legacy/build/pdf.worker.min.mjs?url';
import type {
  IPdfJsDocument,
  IPdfJsLoadingTask,
  IPdfJsPage,
} from 'pdfjs-dist/legacy/build/pdf.min.mjs';
import {
  isTallPage,
  PART_MAX_WIDTH,
  PART_MIN_WIDTH,
  PARTS_PER_PAGE_MAX,
} from '@/domain/menu-import/tall-pages';
import { Globals } from '@/modules/Globals';
import {
  canvasToJpeg,
  createCanvas,
  readBlobAsArrayBuffer,
  releaseCanvas,
} from './capture-dom';
import { pdfjsWasmUrl } from './pdfjs-wasm';
import { splitTallPage } from './tall-parts';

/** Long edge of a rendered page, in pixels. */
export const PDF_PAGE_LONG_EDGE = 1800;
/** Pixels per point of an A4 page rendered at PDF_PAGE_LONG_EDGE: the text scale of very tall pages. */
export const PDF_TEXT_SCALE = PDF_PAGE_LONG_EDGE / 842;
export const PDF_JPEG_QUALITY = 0.8;
/**
 * The pdf.js 6 decoders (JPEG 2000, JBIG2, colour profiles), served by the
 * `pdfjs-wasm` plugin of `vite.config.js`; without them the images of many
 * scanned PDFs would render blank.
 */
export const PDFJS_WASM_BASE = pdfjsWasmUrl(
  Globals.basePath,
  window.location.href
);

export interface IPdfPageImage {
  blob: Blob;
  width: number;
  height: number;
  /** 1-based page number inside the PDF. */
  pageNumber: number;
  /** 1-based part of a very tall page read in parts (`tall-pages.ts`); null for a whole page. */
  part: number | null;
}

export interface IPdfRasterResult {
  /** Pages rendered. */
  pages: number;
  /** The PDF has more pages than `maxPages`. */
  truncated: boolean;
  /** A very tall page needed more parts than allowed: its end is left out. */
  partsTruncated: boolean;
}

/** A password-protected, corrupt or unrenderable PDF. */
export class PdfUnreadableError extends Error {
  constructor() {
    super('PDF_UNREADABLE');
    this.name = 'PdfUnreadableError';
  }
}

interface IOpenedPdf {
  task: IPdfJsLoadingTask;
  pdf: IPdfJsDocument;
}

async function openPdf(file: File): Promise<IOpenedPdf> {
  // The legacy build (minified entry; see pdfjs-legacy.d.ts) supports the
  // admin's older browser targets.
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.min.mjs');
  pdfjs.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
  const data = await readBlobAsArrayBuffer(file);
  const task = pdfjs.getDocument({ data, wasmUrl: PDFJS_WASM_BASE });
  try {
    return { task, pdf: await task.promise };
  } catch {
    await task.destroy().catch(() => undefined);
    throw new PdfUnreadableError();
  }
}

/** One page as a JPEG whose long edge is `PDF_PAGE_LONG_EDGE` (small pages are rendered larger). */
async function renderPage(
  page: IPdfJsPage,
  pageNumber: number
): Promise<IPdfPageImage> {
  const base = page.getViewport({ scale: 1 });
  const viewport = page.getViewport({
    scale: PDF_PAGE_LONG_EDGE / Math.max(base.width, base.height),
  });
  const width = Math.max(1, Math.round(viewport.width));
  const height = Math.max(1, Math.round(viewport.height));
  const canvas = createCanvas(width, height);
  try {
    await page.render({ canvas, viewport }).promise;
    const blob = await canvasToJpeg(canvas, PDF_JPEG_QUALITY);
    return { blob, width, height, pageNumber, part: null };
  } finally {
    releaseCanvas(canvas);
  }
}

/**
 * A very tall page (a website saved as one PDF page) in parts at the text
 * scale of an A4 page, each window rendered on its own (`tall-parts.ts`).
 * Render failures are `PdfUnreadableError`; errors thrown by `onPage`
 * propagate unchanged. Returns whether parts were left out.
 */
async function renderTallPage(
  page: IPdfJsPage,
  pageNumber: number,
  onPage: (page: IPdfPageImage) => Promise<void>,
  maxParts: number
): Promise<boolean> {
  const base = page.getViewport({ scale: 1 });
  const scale = Math.max(
    Math.min(PDF_TEXT_SCALE, PART_MAX_WIDTH / base.width),
    PART_MIN_WIDTH / base.width
  );
  const result = await splitTallPage(
    {
      width: Math.max(1, Math.round(base.width * scale)),
      height: Math.max(1, Math.round(base.height * scale)),
      drawWindow: async (canvas, top) => {
        try {
          const viewport = page.getViewport({ scale, offsetY: -top });
          await page.render({ canvas, viewport }).promise;
        } catch {
          throw new PdfUnreadableError();
        }
      },
    },
    PDF_JPEG_QUALITY,
    (part) =>
      onPage({
        blob: part.blob,
        width: part.width,
        height: part.height,
        pageNumber,
        part: part.part,
      }),
    maxParts
  );
  return result.truncated;
}

/**
 * Renders the PDF one page at a time (bounded memory on phones) and hands
 * each page to `onPage` before rendering the next one; stops after
 * `maxPages`. A very tall page is handed over in parts (at most `maxParts`
 * each). Throws `PdfUnreadableError` for a password-protected or corrupt
 * file; errors thrown by `onPage` propagate unchanged.
 */
export async function rasterizePdf(
  file: File,
  onPage: (page: IPdfPageImage) => Promise<void>,
  maxPages: number,
  maxParts: number = PARTS_PER_PAGE_MAX,
  /** A very tall page needed more than `maxParts` parts: told right after its last one. */
  onPageCut: (pageNumber: number) => void = () => undefined
): Promise<IPdfRasterResult> {
  const { task, pdf } = await openPdf(file);
  try {
    const pages = Math.max(0, Math.min(pdf.numPages, maxPages));
    let partsTruncated = false;
    for (let pageNumber = 1; pageNumber <= pages; pageNumber++) {
      let page: IPdfJsPage | null = null;
      try {
        try {
          page = await pdf.getPage(pageNumber);
        } catch {
          throw new PdfUnreadableError();
        }
        const base = page.getViewport({ scale: 1 });
        if (isTallPage(base.width, base.height)) {
          if (await renderTallPage(page, pageNumber, onPage, maxParts)) {
            partsTruncated = true;
            onPageCut(pageNumber);
          }
          continue;
        }
        let image: IPdfPageImage;
        try {
          image = await renderPage(page, pageNumber);
        } catch {
          throw new PdfUnreadableError();
        }
        page.cleanup();
        page = null;
        await onPage(image);
      } finally {
        page?.cleanup();
      }
    }
    return { pages, truncated: pdf.numPages > pages, partsTruncated };
  } finally {
    // Frees the document and its worker; a failure here must not hide the
    // real outcome.
    await task.destroy().catch(() => undefined);
  }
}
